import { useEffect, useRef, type RefObject } from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

/**
 * Scroll-to-top on route change, and scroll RESTORATION on Back, for an
 * in-app scroll container.
 *
 * The app's scroll container is `<main>`, not the document. That has two
 * consequences the browser can't help with:
 *
 * - `window.scrollTo` is a no-op for it, so a route change has to scroll the
 *   element explicitly (the window is still scrolled as a fallback for any page
 *   that bypasses the container);
 * - the browser's own scroll restoration only knows about the document, so Back
 *   onto a long list always landed at the top of it. That is the other half of
 *   "Back puts me back where I was", next to the panels that live in the URL.
 *   `<ScrollRestoration/>` would not help either, for the same reason.
 *
 * Offsets are recorded per history entry as the user scrolls and replayed on
 * POP. `location.key` is the identity, not the pathname: `/tasks` and
 * `/tasks?task=42` are two entries on one path. For the same reason the
 * scroll-to-top fires on the pathname CHANGING — opening a panel must not throw
 * the list underneath it back to the top.
 */
export function useScrollRestoration(ref: RefObject<HTMLElement | null>): void {
  const location = useLocation()
  const navigationType = useNavigationType()

  const offsets = useRef(new Map<string, number>())
  const locationKeyRef = useRef(location.key)
  locationKeyRef.current = location.key
  const restoringRef = useRef(false)
  const prevPathnameRef = useRef(location.pathname)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const onScroll = () => {
      // Ignore our own scrollTo while a restore is in flight. A container whose
      // content hasn't finished loading clamps scrollTop to its short height,
      // and recording that would overwrite the offset we're trying to reach.
      if (restoringRef.current) return
      offsets.current.set(locationKeyRef.current, el.scrollTop)
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => el.removeEventListener('scroll', onScroll)
  }, [ref])

  useEffect(() => {
    const previousPathname = prevPathnameRef.current
    prevPathnameRef.current = location.pathname
    if (location.hash) return

    const target = navigationType === 'POP' ? offsets.current.get(location.key) : undefined

    if (target != null && target > 0) {
      // The list is normally still fetching when this runs, so the container is
      // too short to take the offset on the first frame. Keep trying briefly.
      restoringRef.current = true
      let frames = 0
      let raf = 0
      const attempt = () => {
        const el = ref.current
        if (!el) { restoringRef.current = false; return }
        el.scrollTo({ top: target, left: 0, behavior: 'auto' })
        if (Math.abs(el.scrollTop - target) < 1 || ++frames > 30) {
          restoringRef.current = false
          return
        }
        raf = requestAnimationFrame(attempt)
      }
      attempt()
      return () => { cancelAnimationFrame(raf); restoringRef.current = false }
    }

    // A query-string change on the same page is not a navigation to the top.
    if (location.pathname === previousPathname) return
    ref.current?.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
  }, [location.key]) // eslint-disable-line react-hooks/exhaustive-deps
}
